import { ApiService } from '../apiService'
import type { AdminUserDataChangeResultDto } from '../types/userData'
import type {
  AdminStoryPageDto,
  AdminStoryReviewPageDto,
  AdminStoryRowDto,
  AdminUserReferralsDto,
  AdventureStoryAccessDto,
  ContentEntitlementDto,
  EntitlementStoryOptionDto,
  GrantEntitlementRequest,
  RevokeEntitlementRequest,
  ReviewVisibilityFilter,
  StoryReviewFilter,
  StorySort,
  StoryVisibilityFilter,
} from '../types/stories'

/**
 * Story moderation (Backlog 060) — AdminStoriesController, route `api/stories` on the Admin API — and user
 * referrals (061). `/admin/x` is rewritten by client.ts to admapi.hadv.quest/api/x.
 *
 * The portal moderates only: the review queue (approve, reject with a reason), Featured rank (editorial, Admin
 * only), hide/unhide with a reason, and review removal. Story content is edited on the Website. Paged reads take a 1-BASED page.
 */
export const storiesApi = {
  list: (params: {
    search?: string
    visibility?: StoryVisibilityFilter
    review?: StoryReviewFilter
    sort?: StorySort
    page: number
    pageSize: number
  }) => ApiService.get<AdminStoryPageDto>('/admin/stories', params),

  /** Approve a Story: listed while public and active. Its editors get a notice. Moderator or Admin. */
  approve: (id: string) => ApiService.post<AdminStoryRowDto>(`/admin/stories/${id}/approve`, {}),

  /** Reject a Story; the reason (required) is shown to its editors. Moderator or Admin. */
  reject: (id: string, reason: string) => ApiService.post<AdminStoryRowDto>(`/admin/stories/${id}/reject`, { reason }),

  setFeaturedRank: (id: string, featuredRank: number | null) =>
    ApiService.put<AdminStoryRowDto>(`/admin/stories/${id}/featured-rank`, { featuredRank }),

  setVisibility: (id: string, isPublic: boolean, reason: string) =>
    ApiService.post<AdminStoryRowDto>(`/admin/stories/${id}/visibility`, { isPublic, reason }),

  getReviews: (id: string, page: number, pageSize: number, visibility: ReviewVisibilityFilter = 'all') =>
    ApiService.get<AdminStoryReviewPageDto>(`/admin/stories/${id}/reviews`, { page, pageSize, visibility }),

  deleteReview: (id: string, reviewId: string, reason: string) =>
    ApiService.post<AdminUserDataChangeResultDto>(`/admin/stories/${id}/reviews/${reviewId}/delete`, { reason }),

  getAdventureAccess: (adventureId: string) =>
    ApiService.get<AdventureStoryAccessDto>(`/admin/adventures/${adventureId}/story-access`),

  getUserReferrals: (userId: string, page: number, pageSize: number) =>
    ApiService.get<AdminUserReferralsDto>(`/admin/users/${userId}/referrals`, { page, pageSize }),

  /** A user's Story/chapter entitlements: live first, newest first. Moderator or Admin. */
  getUserEntitlements: (userId: string) =>
    ApiService.get<ContentEntitlementDto[]>(`/admin/users/${userId}/entitlements`),

  /** Grant a whole Story (achievementId null) or one chapter. Admin only; idempotent. */
  grantEntitlement: (userId: string, body: GrantEntitlementRequest) =>
    ApiService.post<ContentEntitlementDto>(`/admin/users/${userId}/entitlements`, body),

  /** Revoke one entitlement. Admin only; idempotent. */
  revokeEntitlement: (userId: string, entitlementId: string, body: RevokeEntitlementRequest) =>
    ApiService.post<ContentEntitlementDto>(`/admin/users/${userId}/entitlements/${entitlementId}/revoke`, body),

  /** Story picker for grants: at most 25, each with its chapters. */
  searchEntitlementStories: (q: string) =>
    ApiService.get<EntitlementStoryOptionDto[]>('/admin/entitlements/stories', { q }),
}

/** The Story's page on the play site. */
export const storyPlaySiteUrl = (id: string) => `https://hadv.quest/stories/${id}`
